import { createSlice } from "@reduxjs/toolkit";
import { TIMEZONE_LIST } from "const/timezoneList";
import {
  defaultCloneJob,
  defaultEnterprise,
  defaultManageOrg,
  defaultOrganizationsByEnterprise,
  defaultSite,
  defaultSitesByOrganization,
  defaultOrganizationClonePlans,
  defaultStandaloneOrg,
} from "config/bogus/manageScope/const";

const initialState = {
  // `data.sites` is retained for current consumers such as the devices mock.
  data: defaultSite.data,
  memberships: {
    data: {
      enterprises: defaultEnterprise.data,
      standaloneOrgs: defaultStandaloneOrg.data,
    },
  },
  organizations: defaultManageOrg,
  organizationsByEnterprise: defaultOrganizationsByEnterprise,
  sitesByOrganization: defaultSitesByOrganization,
  organizationClonePlans: defaultOrganizationClonePlans,
  cloneSource: {
    data: {
      site: defaultSite.data.sites[0],
    },
  },
  cloneNameCheck: {
    data: {
      available: true,
    },
  },
  cloneJob: defaultCloneJob,
};

export const scopeLevelSlice = createSlice({
  name: "scopeLevel",
  initialState,
  reducers: {
    addScopeLevelSite: (state, action) => {
      state.data.sites.push(action.payload);
    },
    createMockMsp: (state, action) => {
      const name = action.payload?.data?.entName?.trim() || "New MSP";
      const licenseKeys = action.payload?.data?.licenseKeys || [];
      const sequence = state.memberships.data.enterprises.length + 1;

      state.memberships.data.enterprises.push({
        entId: `ent-mock-${sequence}`,
        entName: name,
        licenseKeys,
        createdAt: new Date().toISOString(),
        entRoleId: 1,
        entRoleName: "Super Admin",
        orgNum: 0,
        siteNum: 0,
        adoptedDeviceNum: { ap: 0, switch: 0 },
        inventoryDeviceNum: { ap: 0, switch: 0 },
        clientNum: { wired: 0, wireless: 0 },
        alertNum: { critical: 0, normal: 0, low: 0 },
      });
      state.organizationsByEnterprise[`ent-mock-${sequence}`] = { data: { orgs: [] } };
    },
    createMockStandaloneOrg: (state, action) => {
      const name = action.payload?.data?.orgName?.trim() || "New Organization";
      const sequence = state.memberships.data.standaloneOrgs.length + 1;

      state.memberships.data.standaloneOrgs.push({
        entId: `org-mock-${sequence}`,
        orgId: `org-mock-${sequence}`,
        orgName: name,
        createdAt: new Date().toISOString(),
        orgRoleId: 5,
        orgRoleName: "Admin",
        orgNum: 1,
        siteNum: 0,
        adoptedDeviceNum: { ap: 0, switch: 0 },
        inventoryDeviceNum: { ap: 0, switch: 0 },
        clientNum: { wired: 0, wireless: 0 },
        alertNum: { critical: 0, normal: 0, low: 0 },
      });
      state.sitesByOrganization[`org-mock-${sequence}`] = { data: { sites: [] } };
    },
    createMockManagedOrg: (state, action) => {
      const name = action.payload?.data?.orgName?.trim() || "New Organization";
      const entId = action.payload?.entId;
      const organizationList = state.organizationsByEnterprise[entId]
        || (state.organizationsByEnterprise[entId] = { data: { orgs: [] } });
      const sequence = organizationList.data.orgs.length + 1;

      const orgId = `managed-org-mock-${sequence}`;
      organizationList.data.orgs.push({
        orgId,
        orgName: name,
        createdAt: new Date().toISOString(),
        orgRoleId: 5,
        orgRoleName: "Admin",
        siteNum: 0,
        adoptedDeviceNum: { ap: 0, switch: 0 },
        clientNum: { wired: 0, wireless: 0 },
        alertNum: { critical: 0, normal: 0, low: 0 },
      });
      state.sitesByOrganization[orgId] = { data: { sites: [] } };

      const enterprise = state.memberships.data.enterprises.find(
        (item) => item.entId === entId,
      );
      if (enterprise) enterprise.orgNum += 1;
    },
    createMockOrganizationClone: (state, action) => {
      const entId = action.payload?.entId;
      const data = action.payload?.data || {};
      const organizationList = state.organizationsByEnterprise[entId]
        || (state.organizationsByEnterprise[entId] = { data: { orgs: [] } });
      const sequence = organizationList.data.orgs.length + 1;
      const orgId = `managed-org-clone-${sequence}`;
      const siteNum = state.organizationClonePlans[data.sourceOrgId]?.data?.sites?.length || 0;

      organizationList.data.orgs.push({
        orgId,
        orgName: data.orgName || "Cloned Organization",
        createdAt: new Date().toISOString(),
        orgRoleId: 5,
        orgRoleName: "Admin",
        siteNum,
        adoptedDeviceNum: { ap: 0, switch: 0 },
        clientNum: { wired: 0, wireless: 0 },
        alertNum: { critical: 0, normal: 0, low: 0 },
      });
      const sourceSites = state.sitesByOrganization[data.sourceOrgId]?.data?.sites || [];
      state.sitesByOrganization[orgId] = {
        data: {
          sites: sourceSites.map((site, index) => ({
            ...site,
            siteId: `${orgId}-site-${index + 1}`,
          })),
        },
      };

      const enterprise = state.memberships.data.enterprises.find(
        (item) => item.entId === entId,
      );
      if (enterprise) {
        enterprise.orgNum += 1;
        enterprise.siteNum += siteNum;
      }
    },
    createMockSite: (state, action) => {
      const name = action.payload?.data?.name?.trim() || "New Site";
      const orgId = action.payload?.orgId;
      const countryIndex = action.payload?.data?.countryDomain?.countryIndex;
      const country = TIMEZONE_LIST.find(
        (item) => item.countryIndex === countryIndex,
      )?.name || "";
      const siteList = state.sitesByOrganization[orgId]
        || (state.sitesByOrganization[orgId] = { data: { sites: [] } });
      const sequence = siteList.data.sites.length + 1;

      siteList.data.sites.push({
        siteId: `site-mock-${sequence}`,
        siteName: name,
        siteRoleId: 9,
        siteRoleName: "Admin",
        country,
        timeZone: action.payload?.data?.timeConfig?.timezone ?? "",
        apNum: { online: 0, offline: 0, onboarding: 0 },
        switchNum: { online: 0, offline: 0, onboarding: 0 },
        routerNum: { online: 0, offline: 0, onboarding: 0 },
        clientNum: { wireless: 0, wired: 0 },
        alertNum: { critical: 0, normal: 0, low: 0 },
        createdAt: new Date().toISOString(),
      });

      const standaloneOrg = state.memberships.data.standaloneOrgs.find(
        (item) => item.orgId === orgId,
      );
      if (standaloneOrg) {
        standaloneOrg.siteNum += 1;
        return;
      }

      Object.entries(state.organizationsByEnterprise).some(([entId, orgData]) => {
        const organization = orgData.data.orgs.find((item) => item.orgId === orgId);
        if (!organization) return false;
        organization.siteNum += 1;
        const enterprise = state.memberships.data.enterprises.find(
          (item) => item.entId === entId,
        );
        if (enterprise) enterprise.siteNum += 1;
        return true;
      });
    },
  },
});

export const {
  addScopeLevelSite,
  createMockMsp,
  createMockStandaloneOrg,
  createMockManagedOrg,
  createMockOrganizationClone,
  createMockSite,
} = scopeLevelSlice.actions;

export default scopeLevelSlice.reducer;
